import { QrCode, ShieldAlert, HandCoins, Copy } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { useContent } from "../lib/useContent";
import { resolveMedia } from "../lib/api";
import { Reveal } from "./Reveal";

function Row({ label, value, copyable = false, testid }) {
  const copy = () => {
    navigator.clipboard?.writeText(value).then(() => toast.success(value));
  };
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-ngo-deep/10 last:border-0">
      <span className="text-sm text-ngo-ink/60 shrink-0">{label}</span>
      <span data-testid={testid} className="text-sm font-semibold text-ngo-ink text-right break-all flex items-center gap-1.5">
        {value}
        {copyable && (
          <button onClick={copy} data-testid={`${testid}-copy`} aria-label="कॉपी करें" className="text-ngo-leaf hover:text-ngo-deep transition-colors duration-200">
            <Copy size={13} />
          </button>
        )}
      </span>
    </div>
  );
}

export default function DonateCard({ compact = false }) {
  const { t } = useLang();
  const c = useContent();

  return (
    <Reveal className="w-full">
      <div data-testid="donate-card" className="bg-white rounded-3xl border border-ngo-deep/10 shadow-[0_20px_60px_rgba(23,107,58,0.12)] overflow-hidden">
        <div className="bg-ngo-deep px-6 py-4 flex items-center gap-3">
          <HandCoins className="text-ngo-gold" size={22} />
          <h3 className="font-heading font-bold text-white text-lg">{t("donate.online")}</h3>
        </div>
        <div className={`p-6 sm:p-8 grid gap-8 ${compact ? "" : "md:grid-cols-[240px_1fr]"}`}>
          <div className="flex flex-col items-center text-center">
            <div className="w-52 h-52 rounded-2xl border-2 border-dashed border-ngo-leaf/40 bg-ngo-mint/50 flex flex-col items-center justify-center gap-3 p-4">
              {c.donation_qr ? (
                <img src={resolveMedia(c.donation_qr)} alt="दान QR कोड" data-testid="donation-qr-img" className="w-full h-full object-contain rounded-xl" />
              ) : (
                <>
                  <QrCode size={64} className="text-ngo-deep/50" />
                  <p data-testid="donation-qr-placeholder" className="text-xs text-ngo-ink/60 leading-relaxed">{t("donate.qrPlaceholder")}</p>
                </>
              )}
            </div>
            <p className="mt-3 text-sm text-ngo-ink/70">{t("donate.scanQr")}</p>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-ngo-forest mb-3">{t("donate.bankTitle")}</h4>
            <Row label={t("donate.holder")} value={c.bank.holder} testid="bank-holder" />
            <Row label={t("donate.account")} value={c.bank.account} copyable testid="bank-account" />
            <Row label={t("donate.ifsc")} value={c.bank.ifsc} copyable testid="bank-ifsc" />
            <Row label={t("donate.branch")} value={c.bank.branch} testid="bank-branch" />
            <Row label={t("donate.accType")} value={c.bank.type} testid="bank-type" />
            <Row label={t("donate.upi")} value={c.bank.upi} copyable testid="bank-upi" />
            <Row label={t("donate.contactL")} value={c.phone} testid="bank-contact" />
            <Row label={t("donate.emailL")} value={c.email} testid="bank-email" />
          </div>
        </div>
        <div className="px-6 sm:px-8 pb-6 space-y-3">
          <p className="flex items-start gap-2 text-xs sm:text-sm text-ngo-forest bg-ngo-mint rounded-xl px-4 py-3">
            <ShieldAlert size={16} className="shrink-0 mt-0.5 text-ngo-gold" />
            {t("donate.warning")}
          </p>
          <p className="text-xs text-ngo-ink/55">{t("donate.note")}</p>
        </div>
      </div>
    </Reveal>
  );
}
